// libs
import React, { useContext, useEffect, useState } from "react";
import { Outlet, createSearchParams, useLoaderData, useLocation, useNavigate } from "react-router-dom";

// components
import { Header } from "./Header";
import { SideMenu } from "./SideMenu";
import { Main } from "./components/Main";
import LoadingModal from "./LoadingModal";
import { Alert } from "@components/Alert";
import { Banner } from "@components/Banner";
import { SnackBar } from "@components/SnackBar";
import { Grid, Typography } from "@mui/material";
import { DrawerHeader } from "./components/DrawerHeader";

// services
import { fetchUserGroupAndSandboxes } from "@services/UserService";

// stores
import { AppContext } from "@store/context/App";
import { UserInfoContext } from "@store/context/UserInfo";

// hooks
import { useAlertMessageHandler } from "@hooks/useErrorMessageHandler";
import { SandboxGroupMembership, UserSandboxAccess } from "@utils/appsync/schema/API";
import { useSnackbarHandler } from "@hooks/useSnackbarHandler";

// constants
import { ErrorEnum } from "@constants/Error";

// styles
import { Action } from "@styles/colors";

const PageContainer: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const userSessionInfo: any = useLoaderData();

    const { username, selectedAllocationType, isUserAuthorized, setUserInfo } = useContext(UserInfoContext);

    const { isLoading, isSideMenuOpen, pageAlert, pageTitle, startLoading, stopLoading, openSideMenu, closeSideMenu } =
        useContext(AppContext);

    const { showErrorAlertMessage } = useAlertMessageHandler();

    const { hideSnackbarMessage } = useSnackbarHandler();

    const [error, setError] = useState<any>([]);

    const [isError, setIsError] = useState<Boolean>(false);

    const updateSandboxInUrl = (data: SandboxGroupMembership, selectedSandbox: UserSandboxAccess): UserSandboxAccess => {
        const selectedSandboxId = new URLSearchParams(location.search).get("sandboxId");
        const selectedSandboxFromUrl = data.sandboxes_accessible?.filter(
            (sandbox: UserSandboxAccess) => sandbox.sandbox_id === selectedSandboxId,
        )?.[0];

        // TODO: If selectedSandbox is null, throw error and navigate to home
        if (!selectedSandboxFromUrl) {
            if (selectedSandboxId) {
                showErrorAlertMessage(ErrorEnum.UNAUTHORIZED);
            }
            navigate({
                pathname: "/",
                search: createSearchParams({
                    sandboxId: data.sandboxes_accessible?.[0]?.sandbox_id,
                    allocationType: selectedAllocationType.key,
                }).toString(),
            });
        } else {
            selectedSandbox = selectedSandboxFromUrl;
        }
        return selectedSandbox;
    };

    const fetchSandboxList = async (username: string, email: string): Promise<void> => {
        if (username) {
            startLoading();
            try {
                const resp: any = await fetchUserGroupAndSandboxes(username);
                if (resp.errors?.length) {
                    setError(resp);
                    setIsError(true);
                    return;
                }
                const data = resp.GetGroupsUserIsMemberOf;
                let selectedSandbox = data.sandboxes_accessible?.[0];
                selectedSandbox = updateSandboxInUrl(data, selectedSandbox);
                setUserInfo({
                    email,
                    username,
                    selectedSandbox,
                    sandboxes: data.sandboxes_accessible ?? [],
                    isUserAuthorized: !!data.sandboxes_accessible?.length,
                });
            } catch (reason: any) {
                setError([{ errorType: reason.message }]);
                setIsError(true);
            }

            stopLoading();
        }
    };

    useEffect(() => {
        // by default hide the side nav on Landing/Home page and enable on page other than
        // landing page.
        location.pathname.trim() === "/" ? closeSideMenu() : openSideMenu();
    }, [location]);

    useEffect(() => {
        // Skip API if username already exists in context (navigating within app)
        if (!userSessionInfo.cognitoInfo || username) {
            return;
        }

        const usernameFromSession = (userSessionInfo.cognitoInfo.username || "").replace(
            userSessionInfo.settingsData.cognito.providerName + "_",
            "",
        );
        const email = userSessionInfo.cognitoInfo.signInUserSession?.idToken?.payload?.email || "";
        fetchSandboxList(usernameFromSession, email);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSessionInfo, username]);

    useEffect(() => {
        if (isError && error?.errors) {
            showErrorAlertMessage(error.errors);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError, error]);

    return (
        <React.Fragment>
            {pageAlert.bannerMessage && <Banner {...pageAlert.bannerMessage} />}
            {pageAlert.snackbarMessage && (
                <SnackBar
                    {...pageAlert.snackbarMessage}
                    onClose={() => {
                        hideSnackbarMessage();
                    }}
                />
            )}
            <Header isUserAuthorized={isUserAuthorized} />
            {isUserAuthorized && <SideMenu />}
            <Main
                open={isSideMenuOpen}
                key={location.pathname}
                isUserAuthorized={isUserAuthorized}
                sx={{
                    padding: isSideMenuOpen ? "16px 8px 16px 0" : "16px",
                    position: "relative",
                }}>
                <Grid container sx={{ flexDirection: "column" }}>
                    <Grid
                        item
                        sx={{
                            flexGrow: 1,
                            maxWidth: "100%",
                        }}>
                        <DrawerHeader />
                    </Grid>

                    <Grid item>{pageAlert.alertMessage && <Alert {...pageAlert.alertMessage} />}</Grid>

                    <Grid item mb={2} sx={{ display: "flex", alignItems: "baseline", columnGap: "4px" }}>
                        {!!pageTitle?.title && <Typography variant="h6">{pageTitle.title}</Typography>}
                        {!!pageTitle?.subTitle && (
                            <Typography variant="caption" color={Action[60]}>
                                {pageTitle.subTitle}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item sx={{ width: "100%" }}>
                        {!!username && <Outlet />}
                    </Grid>
                </Grid>
            </Main>
            <LoadingModal isLoading={isLoading} />
        </React.Fragment>
    );
};

export default PageContainer;
